import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import OvTextField from '../atoms/OvTextField';
import OvRecordEditDialog from './OvRecordEditDialog';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoDetailsValues } from '../../../common/model/type/video-details.types';
import { toVideoDetailsUpdate } from '../../../common/utils/video-details-utils';
import { useAdminVideoMutations } from '../../../hooks/use-admin-video-mutations';

interface OvEditVideoDetailsDialogProps {
  video: AdminVideo;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const OvEditVideoDetailsDialog: FC<OvEditVideoDetailsDialogProps> = ({
  video,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { update } = useAdminVideoMutations();

  const formik = useFormik<VideoDetailsValues>({
    initialValues: {
      title: video.title,
      description: video.description ?? '',
    },
    enableReinitialize: true,
    validate: ({ title }) =>
      title.trim() ? {} : { title: t('videos.details.titleRequired') },
    onSubmit: (values) =>
      update.mutate(
        { id: video.id, request: toVideoDetailsUpdate(video, values) },
        {
          onSuccess: () => {
            close();
            onSaved();
          },
        }
      ),
  });

  const close = () => {
    formik.resetForm();
    update.reset();
    onClose();
  };

  const titleError = formik.touched.title ? formik.errors.title : undefined;

  return (
    <OvRecordEditDialog
      isOpen={isOpen}
      title={t('videos.details.editDetails')}
      isDirty={formik.dirty}
      isSaving={update.isPending}
      hasError={update.isError}
      showLiveNotice
      onSubmit={formik.submitForm}
      onClose={close}
    >
      <OvTextField
        id="video-details-title"
        label={t('videos.fields.title')}
        fullWidth
        required
        error={!!titleError}
        helperText={titleError}
        {...formik.getFieldProps('title')}
      />
      <OvTextField
        id="video-details-description"
        label={t('videos.fields.description')}
        fullWidth
        multiline
        minRows={3}
        {...formik.getFieldProps('description')}
      />
    </OvRecordEditDialog>
  );
};

export default OvEditVideoDetailsDialog;
